import { motion } from "framer-motion";
import {
  Bot, BrainCircuit, Workflow, LineChart, LayoutDashboard, Terminal,
  Database, Sparkles, Play,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const features = [
  { name: "AI Agents", desc: "Autonomous workers across sales, ops, and support.", icon: Bot, color: "from-neon-cyan to-neon-blue" },
  { name: "AI Assistants", desc: "Embedded copilots inside every team tool.", icon: BrainCircuit, color: "from-neon-purple to-neon-magenta" },
  { name: "Business Automation", desc: "Reliable orchestration across your full stack.", icon: Workflow, color: "from-neon-orange to-neon-magenta" },
  { name: "CRM Intelligence", desc: "Self-cleaning pipelines + revenue copilots.", icon: Database, color: "from-neon-blue to-neon-purple" },
  { name: "Predictive Analytics", desc: "Forecast revenue, churn, and demand.", icon: LineChart, color: "from-emerald-400 to-neon-cyan" },
  { name: "Internal AI Tools", desc: "Custom-built copilots for your team.", icon: LayoutDashboard, color: "from-neon-magenta to-neon-purple" },
];

const log = [
  { t: "12:04:21", c: "#00F0FF", m: "agent.lead-router started" },
  { t: "12:04:22", c: "#FF007F", m: "→ enrich(\"acme.io\") • 84 fields" },
  { t: "12:04:23", c: "#00F0FF", m: "score=A+ • route → AE.priya" },
  { t: "12:04:24", c: "#34d399", m: "✓ Slack notified • CRM updated" },
  { t: "12:04:26", c: "#00F0FF", m: "agent.followup queued · 24h" },
  { t: "12:04:28", c: "#FF5E00", m: "metric.ARR += ₹42,000" },
  { t: "12:04:31", c: "#8A2BE2", m: "agent.qbr-summary booted" },
  { t: "12:04:33", c: "#00F0FF", m: "✓ deck draft v3 ready" },
];

export default function AiLabPage() {
  return (
    <div data-testid="ai-lab-root">
      <PageHero
        eyebrow="AI & Automation Lab"
        title={<>The growth ops team you <span className="text-gradient-neon">can&apos;t hire.</span></>}
        subtitle="Our in-house lab designs and ships AI systems that do the unglamorous work — 24/7, with the precision of a senior operator."
      >
        <div className="relative inline-flex items-center gap-3 glass rounded-full pl-2 pr-4 py-2">
          <span className="relative w-8 h-8 rounded-full bg-gradient-to-tr from-neon-cyan to-neon-purple flex items-center justify-center">
            <Bot size={16} />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
          </span>
          <span className="font-mono text-xs tracking-[0.18em] uppercase text-white/80">
            6 agents live · 142 actions / hour
          </span>
        </div>
      </PageHero>

      {/* Conic orb backdrop section */}
      <section className="section pt-0 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full opacity-30 blur-3xl bg-[conic-gradient(from_0deg,#00F0FF,#8A2BE2,#FF007F,#FF5E00,#00F0FF)] spin-slow" />
        </div>

        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Feature grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  data-testid={`ai-feature-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="glass rounded-2xl p-6 relative overflow-hidden group hover:border-white/20"
                >
                  <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl opacity-25 bg-gradient-to-tr ${f.color}`} />
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center`}>
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold tracking-tight">{f.name}</h3>
                  <p className="mt-1.5 text-sm text-white/55">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Live terminal */}
          <div className="lg:col-span-5">
            <div className="glass rounded-2xl overflow-hidden h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex items-center gap-2 text-[11px] font-mono text-white/50">
                  <Terminal size={12} /> novarise.agents.live
                </div>
                <span className="text-[11px] font-mono text-emerald-400">● running</span>
              </div>
              <div className="p-5 font-mono text-[12.5px] leading-7">
                {log.map((l, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.18 }}
                    className="flex gap-3"
                  >
                    <span className="text-white/30">{l.t}</span>
                    <span style={{ color: l.c }}>{l.m}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo card row */}
      <section className="section relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="overline mb-4 flex items-center gap-2"><Sparkles size={12} /> Interactive previews</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Two systems we&apos;ve already shipped.</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <DemoCard
              title="Workflow Builder"
              desc="Drag-and-drop multi-step automations across CRM, comms and AI."
              color="from-neon-cyan to-neon-blue"
              kind="workflow"
            />
            <DemoCard
              title="Automation Studio"
              desc="Live observability across every running agent and workflow."
              color="from-neon-magenta to-neon-purple"
              kind="studio"
            />
          </div>
        </div>
      </section>

      <CTABand
        headline={<>Bring AI into your <span className="text-gradient-warm">operating system.</span></>}
        sub="We design, ship, and operate AI systems for your business — end to end."
        primary={{ label: "Talk to the AI Lab", to: "/contact" }}
        secondary={{ label: "See Future Products", to: "/products" }}
      />
    </div>
  );
}

function DemoCard({ title, desc, color, kind }) {
  return (
    <div className="relative glass rounded-3xl p-6 overflow-hidden">
      <div className={`absolute -top-32 -right-32 w-72 h-72 rounded-full blur-3xl opacity-25 bg-gradient-to-tr ${color}`} />
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">{title}</h3>
        <button className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white">
          <Play size={12} /> Preview
        </button>
      </div>
      <p className="mt-1 text-sm text-white/55">{desc}</p>

      {/* Mockup */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-black/40 p-4">
        {kind === "workflow" ? (
          <div className="space-y-3">
            {["Trigger: New lead", "Enrich + Score", "Route → AE", "Send AI follow-up"].map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-7 h-7 rounded-md bg-gradient-to-br ${color} flex items-center justify-center text-[11px] font-mono`}>0{i + 1}</span>
                <span className="text-sm">{s}</span>
                <span className="ml-auto font-mono text-[10px] text-emerald-400">● ok</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Agents online", v: "6 / 6" },
              { label: "Actions / hour", v: "142" },
              { label: "Avg latency", v: "1.4s" },
              { label: "Success rate", v: "99.2%" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">{s.label}</div>
                <div className="mt-1.5 font-mono text-lg font-extrabold">{s.v}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
